import React, { useState, useEffect, useRef, useMemo, useContext, useCallback } from 'react';
import { Search, Plus, Edit2, Trash2, ChevronDown, Filter, X, Settings, ArrowUp, ArrowDown, Check } from 'lucide-react';
import Dashboard from '../../Layouts/Dashboard';
import { useDashboard } from '../../Layouts/Dashboard';
import {
  Box,
  Paper,
  Typography,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Chip
} from '@mui/material';
import { HttpRequestContext } from '../../Blocks/HttpRequestProvider';
import { format } from 'date-fns';
import ClientTable from './Blocks/ClientTable';

// Define breadcrumbs for this page
const clientManagementBreadcrumbs = [
  { href: '/', label: 'Accueil' },
  { href: '/invoicing', label: 'Facturation' },
  { label: 'Gestion des Clients' }
];

// Storage keys for persistence
const STORAGE_KEY_PREFIX = 'clientManagement_';
const STORAGE_KEYS = {
  COLUMNS: `${STORAGE_KEY_PREFIX}columns`,
  PAGE_SIZE: `${STORAGE_KEY_PREFIX}pageSize`,
  SORT_CONFIG: `${STORAGE_KEY_PREFIX}sortConfig`,
  FILTER_ACTIVE: `${STORAGE_KEY_PREFIX}filterActive`,
  SEARCH_TERM: `${STORAGE_KEY_PREFIX}searchTerm`
};

// Default settings
const DEFAULT_COLUMNS = [
  { id: 'clientInfo', name: 'Client / Société', visible: true, sortable: true },
  { id: 'contact', name: 'Contact', visible: true, sortable: true },
  { id: 'tva', name: 'TVA/BCE', visible: true, sortable: true },
  { id: 'status', name: 'Statut', visible: true, sortable: true },
  { id: 'invoiceCount', name: 'Factures', visible: true, sortable: true },
  { id: 'totalBilled', name: 'Total Facturé', visible: true, sortable: true },
  { id: 'invoiceNumber', name: 'Dernière Facture', visible: true, sortable: true },
  { id: 'invoiceStatus', name: 'Statut de Facture', visible: true, sortable: true },
  { id: 'createdAt', name: 'Date de Création', visible: false, sortable: true },
  { id: 'actions', name: 'Actions', visible: true, sortable: false }
];
const DEFAULT_PAGE_SIZE = 5;
const DEFAULT_SORT_CONFIG = { key: null, direction: 'asc' };
const DEFAULT_FILTER_ACTIVE = null;
const DEFAULT_SEARCH_TERM = '';

// Helper to safely parse JSON from localStorage with fallback
const getSavedValue = (key, defaultValue) => {
  try {
    const savedValue = localStorage.getItem(key);
    return savedValue !== null ? JSON.parse(savedValue) : defaultValue;
  } catch (error) {
    console.error(`Error loading saved setting for ${key}:`, error);
    return defaultValue;
  }
};

// Validate saved columns to ensure they all exist in our current schema
const validateSavedColumns = (savedColumns) => {
  // If no saved columns, use defaults
  if (!savedColumns || !Array.isArray(savedColumns) || savedColumns.length === 0) {
    return DEFAULT_COLUMNS;
  }

  // Check if all saved columns have valid IDs
  const validColumnIds = new Set(DEFAULT_COLUMNS.map(col => col.id));
  const allColumnsValid = savedColumns.every(col => 
    col && typeof col === 'object' && 
    validColumnIds.has(col.id) &&
    'visible' in col && 'sortable' in col && 'name' in col
  );

  // If any column is invalid, or we don't have the same number of columns,
  // use the default columns (schema might have changed)
  if (!allColumnsValid || savedColumns.length !== DEFAULT_COLUMNS.length) {
    console.warn('Invalid saved columns detected, using defaults');
    return DEFAULT_COLUMNS;
  }

  return savedColumns;
};

// Validate saved sort configuration
const validateSortConfig = (sortConfig, columns) => {
  // If no config or invalid structure, use default
  if (!sortConfig || typeof sortConfig !== 'object') {
    return DEFAULT_SORT_CONFIG;
  }

  // Get valid column ids that are sortable
  const validSortableColumnIds = columns
    .filter(col => col.sortable)
    .map(col => col.id);
  
  // Check if the sort key is valid
  const isValidSortKey = sortConfig.key === null || 
    (validSortableColumnIds.includes(sortConfig.key));
  
  // Check if the sort direction is valid
  const isValidDirection = sortConfig.direction === 'asc' || sortConfig.direction === 'desc';

  // If sort config is invalid, use default
  if (!isValidSortKey || !isValidDirection) {
    console.warn('Invalid sort configuration detected, using defaults');
    return DEFAULT_SORT_CONFIG;
  }

  return sortConfig;
};

// Validate page size
const validatePageSize = (pageSize) => {
  const validPageSizes = [5, 10, 25, 50];
  if (typeof pageSize !== 'number' || !validPageSizes.includes(pageSize)) {
    return DEFAULT_PAGE_SIZE;
  }
  return pageSize;
};

const ClientManagementContent = ({ setHeaderActions: propSetHeaderActions }) => {
  // Get setHeaderActions from context
  const { setHeaderActions: contextSetHeaderActions } = useDashboard();
  
  // Use the prop version if provided (backward compatibility) or the context version
  const setHeaderActions = propSetHeaderActions || contextSetHeaderActions;
  
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { sendRequest } = useContext(HttpRequestContext);
  const [totalItems, setTotalItems] = useState(0);
  
  // Load and validate saved columns
  const validatedColumns = useMemo(() => {
    // First check if we have columns saved in localStorage using the ClientTable's storage key
    const clientTableSavedColumns = localStorage.getItem('clientManagementTableColumns');
    if (clientTableSavedColumns) {
      try {
        return JSON.parse(clientTableSavedColumns);
      } catch (error) {
        console.error('Error parsing saved columns:', error);
      }
    }
    
    // Fall back to the old storage mechanism if nothing found in new location
    const savedColumns = getSavedValue(STORAGE_KEYS.COLUMNS, DEFAULT_COLUMNS);
    return validateSavedColumns(savedColumns);
  }, []);
  
  const [filterActive, setFilterActive] = useState(() => {
    const saved = getSavedValue(STORAGE_KEYS.FILTER_ACTIVE, DEFAULT_FILTER_ACTIVE);
    return saved === true || saved === false || saved === null ? saved : DEFAULT_FILTER_ACTIVE;
  });
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  
  // Column settings with localStorage persistence and validation
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [availableColumns, setAvailableColumns] = useState(validatedColumns);
  
  // Create refs for popups/dropdowns
  const columnSettingsRef = useRef(null);
  const columnSettingsBtnRef = useRef(null);
  
  // Persist settings when they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.COLUMNS, JSON.stringify(availableColumns));
  }, [availableColumns]);
  
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.FILTER_ACTIVE, JSON.stringify(filterActive));
  }, [filterActive]);
  
  // Fetch clients data on component mount
  const fetchClientsData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await sendRequest({
        apiPath: 'invoicing/clients/with-invoices',
        type: 'GET'
      });
      
      if (response && response.clients) {
        setClients(response.clients);
        setTotalItems(response.client_count || response.clients.length);
      } else {
        console.error('Failed to fetch client data:', response);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setIsLoading(false);
    }
  }, [sendRequest]);

  // Initialize component
  useEffect(() => {
    fetchClientsData();
  }, [fetchClientsData]);
  
  // Handle column visibility toggle
  const toggleColumnVisibility = (columnId) => {
    setAvailableColumns(
      availableColumns.map(col => 
        col.id === columnId ? { ...col, visible: !col.visible } : col
      )
    );
  };
  
  // Handle editing a client
  const openEditModal = (client) => {
    setEditingClient(client);
    setShowAddEditModal(true);
  };

  // Handle deletion
  const handleDelete = (clientId) => {
    const clientEntry = clients.find(item => (item.client && item.client.id === clientId) || item.id === clientId);
    const clientToRemove = clientEntry.client || clientEntry;
    setClientToDelete(clientToRemove);
    setShowDeleteModal(true);
  };
  
  // Confirm client deletion
  const confirmDelete = async () => {
    if (clientToDelete) {
      try {
        // Call the API to delete the client
        const response = await sendRequest({
          apiPath: `invoicing/clients/${clientToDelete.id}`,
          type: 'DELETE'
        });
        
        if (response) {
          // Update the local state only after successful API call
      const newClients = clients.filter(item => {
        // Check if the client is directly in the array or in the nested client property
        const clientId = (item.client && item.client.id) || item.id;
        return clientId !== clientToDelete.id;
      });
      setClients(newClients);
      setShowDeleteModal(false);
      setClientToDelete(null);
          
          // Refresh client list
          fetchClientsData();
        }
      } catch (error) {
        console.error('Error deleting client:', error);
        alert('Une erreur est survenue lors de la suppression du client.');
      }
    }
  };
  
  // Handle modals
  const openAddModal = () => {
    setEditingClient(null);
    setShowAddEditModal(true);
  };
  
  // Handle outside clicks for column settings dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close column settings dropdown when clicking outside
      if (
        showColumnSettings && 
        columnSettingsRef.current && 
        !columnSettingsRef.current.contains(event.target) &&
        columnSettingsBtnRef.current &&
        !columnSettingsBtnRef.current.contains(event.target)
      ) {
        setShowColumnSettings(false);
      }
      
      // Close modals when clicking on their background overlays
      if (
        event.target.classList.contains('modal-overlay')
      ) {
        if (showExportModal) setShowExportModal(false);
        if (showDeleteModal) setShowDeleteModal(false);
        if (showAddEditModal) setShowAddEditModal(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnSettings, showExportModal, showDeleteModal, showAddEditModal]);
  
  // Set header actions when component mounts
  useEffect(() => {
    setHeaderActions(
      <div className="flex gap-2">
        <button 
          ref={columnSettingsBtnRef}
          className="flex items-center gap-2 bg-white border border-gray-300 rounded-md px-4 py-2 hover:bg-gray-50"
          onClick={() => setShowColumnSettings(!showColumnSettings)}
        >
          <Settings size={16} />
          <span>Colonnes</span>
        </button>
        <button 
          className="flex items-center gap-2 bg-gray-100 border border-gray-300 rounded-md px-4 py-2 text-gray-400 cursor-not-allowed"
          disabled
        >
          <Filter size={16} />
          <span>Filtres</span>
          <ChevronDown size={16} />
        </button>
        <button
          className="flex items-center gap-2 bg-gray-100 border border-gray-300 rounded-md px-4 py-2 text-gray-400 cursor-not-allowed"
          disabled
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          <span>Exporter</span>
        </button>
        <button 
          className="flex items-center gap-2 bg-blue-600 text-white rounded-md px-4 py-2 hover:bg-blue-700"
          onClick={openAddModal}
        >
          <Plus size={16} />
          <span>Nouveau Client</span>
        </button>
      </div>
    );
    
    // Cleanup function to reset header actions when component unmounts
    return () => setHeaderActions(null);
  }, [setHeaderActions, showColumnSettings, setShowExportModal, openAddModal]);
  
  // Format date for display
  const getClientName = (client) => {
    // If there is a client name (first and/or last name)
    const hasClientName = (client.firstname || client.lastname);
    
    if (hasClientName && client.entity_name) {
      // Case 1: Both client name and company name exist
      return `${client.firstname || ''} ${client.lastname || ''}`.trim();
    } else if (client.entity_name) {
      // Case 2: Only company name exists
      return client.entity_name;
    } else {
      // Default: Just return available names
      return `${client.firstname || ''} ${client.lastname || ''}`.trim() || 'Unnamed Client';
    }
  };

  // Form submission for client add/edit
  const handleClientFormSubmit = async (e) => {
    e.preventDefault();
    
    // Get form data
    const formData = {
      firstname: document.getElementById('firstname').value,
      lastname: document.getElementById('lastname').value,
      entity_name: document.getElementById('entity_name').value,
      email: document.getElementById('email').value,
      phone: document.getElementById('phone').value,
      status: document.getElementById('status').value,
      address_line1: document.getElementById('address').value,
      address_line2: document.getElementById('address_line2').value,
      vat_number: document.getElementById('vat_number').value,
      bce_number: document.getElementById('bce_number').value
    };
    
    try {
      // Determine if we're creating or editing
      const apiPath = editingClient 
        ? `invoicing/clients/${editingClient.id}` 
        : 'invoicing/clients/create';
        
      const response = await sendRequest({
        apiPath,
        type: 'POST',
        request: formData
      });
      
      if (response && (response.success || response.client || response.id)) {
        // Refresh client list to show the updated data
        fetchClientsData();
        setShowAddEditModal(false);
      } else {
        console.error('Failed to save client:', response);
        alert('Une erreur est survenue lors de la sauvegarde du client.');
      }
    } catch (err) {
      console.error('Error saving client:', err);
      alert('Une erreur est survenue lors de la sauvegarde du client.');
    }
  };

  // Handle column visibility changes
  const handleColumnVisibilityChange = (updatedColumns) => {
    setAvailableColumns(updatedColumns);
    
    // Also update localStorage directly for backup persistence in client management
    localStorage.setItem('clientManagementTableColumns', JSON.stringify(updatedColumns));
  };

  return (
    <div className="max-w-full">
      {/* Column Settings Dropdown */}
      {showColumnSettings && (
        <div 
          ref={columnSettingsRef}
          className="absolute right-6 mt-12 w-64 bg-white rounded-lg shadow-lg z-10 border border-gray-200"
        >
          <div className="p-3 border-b border-gray-200">
            <h3 className="font-medium text-gray-700">Colonnes affichées</h3>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {availableColumns.map(column => (
              <div 
                key={column.id} 
                className="flex items-center justify-between p-3 hover:bg-gray-50"
              >
                <span className="text-sm text-gray-700">{column.name}</span>
                <button 
                  onClick={() => column.id !== 'actions' && toggleColumnVisibility(column.id)}
                  className={`w-5 h-5 rounded flex items-center justify-center ${
                    column.visible ? 'bg-blue-500 text-white' : 'border border-gray-300'
                  } ${column.id === 'actions' ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                  disabled={column.id === 'actions'}
                >
                  {column.visible && <Check size={14} />}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Client Table Component */}
      <ClientTable 
        clients={clients}
        isLoading={isLoading}
        initialColumns={availableColumns}
        onClientListRefresh={fetchClientsData}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        handleClientFetching={false}
        showAddClientModal={false}
        showHeaderButtons={false}
      />
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 modal-overlay">
          <div className="bg-white rounded-lg p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirmer la suppression</h3>
            <p className="text-sm text-gray-500 mb-4">
              Êtes-vous sûr de vouloir supprimer le client <span className="font-medium">{getClientName(clientToDelete)}</span> ?
              Cette action ne peut pas être annulée.
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                onClick={() => setShowDeleteModal(false)}
              >
                Annuler
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                onClick={confirmDelete}
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Add/Edit Client Modal */}
      {showAddEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 modal-overlay">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingClient ? 'Modifier le client' : 'Ajouter un nouveau client'}
            </h3>
            
            <form className="space-y-4" onSubmit={handleClientFormSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstname" className="block text-sm font-medium text-gray-700">Prénom</label>
                  <input
                    type="text"
                    id="firstname"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    defaultValue={editingClient?.firstname || ''}
                  />
                </div>
                <div>
                  <label htmlFor="lastname" className="block text-sm font-medium text-gray-700">Nom</label>
                  <input
                    type="text"
                    id="lastname"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    defaultValue={editingClient?.lastname || ''}
                  />
                </div>
                <div>
                  <label htmlFor="entity_name" className="block text-sm font-medium text-gray-700">Nom de société</label>
                  <input
                    type="text"
                    id="entity_name"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    defaultValue={editingClient?.entity_name || ''}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    id="email"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    defaultValue={editingClient?.email || ''}
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Téléphone</label>
                  <input
                    type="text"
                    id="phone"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    defaultValue={editingClient?.phone || ''}
                  />
                </div>
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700">Statut</label>
                  <select
                    id="status"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    defaultValue={editingClient?.status || "active"}
                  >
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700">Adresse (Ligne 1)</label>
                  <input
                    type="text"
                    id="address"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    defaultValue={editingClient?.address || ''}
                  />
                </div>
                <div>
                  <label htmlFor="address_line2" className="block text-sm font-medium text-gray-700">Adresse (Ligne 2)</label>
                  <input
                    type="text"
                    id="address_line2"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    defaultValue={editingClient?.address_line2 || ''}
                  />
                </div>
                <div>
                  <label htmlFor="vat_number" className="block text-sm font-medium text-gray-700">Numéro TVA</label>
                  <input
                    type="text"
                    id="vat_number"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    defaultValue={editingClient?.vat_number || ''}
                  />
                </div>
                <div>
                  <label htmlFor="bce_number" className="block text-sm font-medium text-gray-700">Numéro BCE</label>
                  <input
                    type="text"
                    id="bce_number"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    defaultValue={editingClient?.bce_number || ''}
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                  onClick={() => setShowAddEditModal(false)}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingClient ? 'Enregistrer' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Wrap the ClientManagementContent component with the Dashboard layout
const ClientManagementPage = () => {
  return (
    <Dashboard 
      dashboardTitle="Gestion des Clients" 
      breadcrumbs={clientManagementBreadcrumbs}
    >
      <ClientManagementContent />
    </Dashboard>
  );
};

export default ClientManagementPage;